// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import loadCustomQuotes from "@ces/App/Handlers/CustomQuotes/LoadCustomQuotes";
import updateSearchFields, {
    GetCustomQuotesApiParameter,
} from "@ces/App/Handlers/RfqMyCustomQuotes/UpdateSearchFields";
import { CustomAppState } from "@ces/App/Store/Reducers";
import { CustomSettingsCollectionModel, getSettingsCollection } from "@ces/App/Store/Selectors/SettingsSelectors";
import { getCustomQuotesDataView } from "@ces/App/Store/Selectors/xQuotesSelector";
import { parseBoolean } from "@ces/XDL/Utilities";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import qs from "qs";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    customQuotesDataView: getCustomQuotesDataView(state, state.xRfqMyQuotes.getCustomQuotesParameter),
    getQuotesParameter: state.xRfqMyQuotes.getCustomQuotesParameter,
    location: getLocation(state),
    enableColonialCustomQuote: (getSettingsCollection(state) as CustomSettingsCollectionModel).customSetting
        .enableColonialCustomQuote,
});

const mapDispatchToProps = {
    loadCustomQuotes,
    updateSearchFields,
};

type Props = HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext &
    PageProps;

const RfqMyCustomQuotesPage = ({
    id,
    customQuotesDataView,
    getQuotesParameter,
    updateSearchFields,
    loadCustomQuotes,
    history,
    location,
    enableColonialCustomQuote,
}: Props) => {
    let firstLoad = false;

    useEffect(() => {
        firstLoad = true;
        let parsedGetQuotesParameter: GetCustomQuotesApiParameter = {};
        if (location.search) {
            parsedGetQuotesParameter = parseQueryString<GetCustomQuotesApiParameter>(location.search);
            if (typeof parsedGetQuotesParameter.statuses === "string") {
                parsedGetQuotesParameter.statuses = [parsedGetQuotesParameter.statuses];
            }
        }

        updateSearchFields({
            ...getQuotesParameter,
            ...parsedGetQuotesParameter,
            type: "Replace",
            sort: "enteredDate DESC, invoiceNumber DESC",
        });
    }, []);

    useEffect(() => {
        if (!firstLoad) {
            const queryString = qs.stringify(getQuotesParameter);
            history.replace(`${location.pathname}${queryString !== "" ? `?${queryString}` : ""}`);
        }
    }, [getQuotesParameter]);

    useEffect(() => {
        // if this is undefined it means someone changed the filters and we haven't loaded the new collection yet
        if (!firstLoad && !customQuotesDataView.value && !customQuotesDataView.isLoading) {
            loadCustomQuotes(getQuotesParameter);
        }
    });

    if (!parseBoolean(enableColonialCustomQuote)) {
        return null;
    }
    return (
        <Page>
            <Zone contentId={id} zoneName="Content" />
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withIsInShell(RfqMyCustomQuotesPage))),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "RfqMyCustomQuotesPage" instead of this constant.
 */
export const RfqMyCustomQuotesPageContext = "RfqMyCustomQuotesPage";
